import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { DEFAULT_LOCALE, translate } from '@formosa/shared/i18n/translate';
import { journeyApi } from '../../api/journeyApi';
import { journeyDecisionApi } from '../../api/journeyDecisionApi';
import { useAuth } from '../../auth/AuthContext';
import { ErrorState, LoadingState } from '../../components/ui';
import { WORKSPACE_PRODUCT_CHROME, DECISIONS_PRODUCT_CHROME } from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import {
  DECISION_TYPES,
  ELIGIBILITY_RULES,
  buildCreateDecisionPayload,
  canCreateDecision,
  classifyDecisionError,
  createDecisionClientRequestId,
  decisionTypeLabel,
  emptyDecisionDraft,
  normalizeJourneyDecision,
  validateDecisionDraft,
} from './journeyDecisionModel';
import '../journey-workspace/journey-workspace.css';
import '../journey-workspace/journey-logistics.css';
import './journey-decision.css';

export default function JourneyDecisionCreatePage() {
  const { journeyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useCallback((key) => translate(DEFAULT_LOCALE, key), []);
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef(false);
  const createRequestIdRef = useRef(null);

  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [draft, setDraft] = useState(emptyDecisionDraft);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');

  const titleId = useId();
  const descriptionId = useId();
  const typeId = useId();
  const eligibilityId = useId();
  const deadlineId = useId();
  const minPartId = useId();

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    setPhase('loading');
    setErrorModel(null);
    try {
      const detailPayload = await journeyApi.getDetail(journeyId);
      if (requestId !== generation.current) return;
      setJourney(normalizeJourneyWorkspace(detailPayload, viewerMemberId));
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyDecisionError(cause);
      const access = classifyJourneyAccessError(cause);
      if (kind === 'journey-not-found' || kind === 'not-found' || access === 'not-found') {
        setPhase('not-found');
      } else if (kind === 'forbidden' || access === 'forbidden') {
        setPhase('forbidden');
      } else {
        setErrorModel(mapApiErrorToUiModel(cause, { t }));
        setPhase('error');
      }
    }
  }, [journeyId, t, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, load]);

  function updateOption(index, patch) {
    setDraft((current) => ({
      ...current,
      options: current.options.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  }

  function addOption() {
    setDraft((current) => ({
      ...current,
      options: [
        ...current.options,
        {
          optionId: null,
          label: '',
          description: '',
          displayOrder: current.options.length + 1,
        },
      ],
    }));
  }

  function removeOption(index) {
    setDraft((current) => {
      if (current.options.length <= 2) return current;
      return {
        ...current,
        options: current.options
          .filter((_, i) => i !== index)
          .map((row, i) => ({ ...row, displayOrder: i + 1 })),
      };
    });
  }

  async function submit(event) {
    event.preventDefault();
    if (mutating.current || busy) return;
    const validation = validateDecisionDraft(draft);
    if (validation) {
      setFormError(validation);
      return;
    }
    if (!createRequestIdRef.current) {
      createRequestIdRef.current = createDecisionClientRequestId();
    }
    const requestId = generation.current;
    mutating.current = true;
    setBusy(true);
    setFormError('');
    try {
      const body = buildCreateDecisionPayload(draft, {
        clientRequestId: createRequestIdRef.current,
      });
      const payload = await journeyDecisionApi.create(journey.journeyId, body);
      if (requestId !== generation.current) return;
      const created = normalizeJourneyDecision(payload);
      createRequestIdRef.current = null;
      navigate(`/journeys/${journey.journeyId}/decisions/${created.decisionId}`, { replace: true });
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyDecisionError(cause);
      if (kind === 'validation') setFormError(DECISIONS_PRODUCT_CHROME.formCheckFallback);
      else if (kind === 'forbidden') setFormError(DECISIONS_PRODUCT_CHROME.createPermissionDenied);
      else if (kind === 'lifecycle') setFormError(DECISIONS_PRODUCT_CHROME.createLifecycleDenied);
      else if (kind === 'idempotency-conflict') {
        setFormError(DECISIONS_PRODUCT_CHROME.createIdempotencyConflict);
        createRequestIdRef.current = createDecisionClientRequestId();
      } else if (kind === 'unauthorized') {
        setFormError(DECISIONS_PRODUCT_CHROME.sessionExpired);
      } else {
        setFormError(
          resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed')
            || DECISIONS_PRODUCT_CHROME.createFailed,
        );
      }
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state">
        <LoadingState label={t('travel.common.loading')} data-testid="decision-create-loading" />
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.notFoundTitle}</h1>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.forbiddenTitle}</h1>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    const errorCopy = resolveUiErrorCopy(errorModel, t);
    return (
      <main className="journey-workspace-state" data-testid="decision-create-error">
        <ErrorState
          title={DECISIONS_PRODUCT_CHROME.openCreateFailedTitle}
          description={errorCopy.description}
          retryable
          retryLabel={DECISIONS_PRODUCT_CHROME.tryAgain}
          onRetry={load}
        />
      </main>
    );
  }

  const allowCreate = canCreateDecision({
    role: journey?.requesterRole,
    lifecycleStatus: journey?.lifecycleStatus,
  });

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{WORKSPACE_PRODUCT_CHROME.eyebrow}</p>
          <h1>{journey?.title || DECISIONS_PRODUCT_CHROME.untitledJourney}</h1>
          <p className="journey-workspace__meta">
            {journey?.lifecycleStatusLabel}
            {journey?.requesterRoleLabel ? ` · ${journey.requesterRoleLabel}` : ''}
          </p>
        </div>
        <Link className="journey-workspace__back" to={`/journeys/${journey.journeyId}/decisions`}>
          {DECISIONS_PRODUCT_CHROME.backToDecisions}
        </Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />

      <section className="journey-workspace__panel journey-decision" aria-labelledby="create-decision-heading">
        <div className="journey-workspace__panel-head">
          <h2 id="create-decision-heading">{DECISIONS_PRODUCT_CHROME.createDecision}</h2>
        </div>

        {!allowCreate ? (
          <p className="journey-decision__lifecycle-note" role="status">
            {DECISIONS_PRODUCT_CHROME.createLifecycleGate}
          </p>
        ) : (
          <form className="journey-decision__form" onSubmit={submit} noValidate>
            <label htmlFor={titleId}>
              問題 / 標題
              <input
                id={titleId}
                value={draft.title}
                maxLength={100}
                onChange={(e) => setDraft((c) => ({ ...c, title: e.target.value }))}
                required
              />
            </label>
            <label htmlFor={descriptionId}>
              說明（選填）
              <textarea
                id={descriptionId}
                rows={3}
                maxLength={1000}
                value={draft.description}
                onChange={(e) => setDraft((c) => ({ ...c, description: e.target.value }))}
              />
            </label>
            <div className="journey-decision__grid-2">
              <label htmlFor={typeId}>
                類型
                <select
                  id={typeId}
                  value={draft.decisionType}
                  onChange={(e) => setDraft((c) => ({ ...c, decisionType: e.target.value }))}
                >
                  {DECISION_TYPES.map((type) => (
                    <option key={type} value={type}>{decisionTypeLabel(type)}</option>
                  ))}
                </select>
              </label>
              <label htmlFor={eligibilityId}>
                誰可以投票
                <select
                  id={eligibilityId}
                  value={draft.eligibilityRule}
                  onChange={(e) => setDraft((c) => ({ ...c, eligibilityRule: e.target.value }))}
                >
                  {ELIGIBILITY_RULES.map((rule) => (
                    <option key={rule} value={rule}>
                      {rule === 'ACTIVE_JOURNEY_MEMBERS'
                        ? DECISIONS_PRODUCT_CHROME.eligibilityAllMembers
                        : DECISIONS_PRODUCT_CHROME.eligibilityOwnersOnly}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="journey-decision__grid-2">
              <label htmlFor={deadlineId}>
                {DECISIONS_PRODUCT_CHROME.deadlineOptional}
                <input
                  id={deadlineId}
                  type="datetime-local"
                  value={draft.deadlineAt}
                  onChange={(e) => setDraft((c) => ({ ...c, deadlineAt: e.target.value }))}
                />
              </label>
              <label htmlFor={minPartId}>
                最低參與人數（選填）
                <input
                  id={minPartId}
                  inputMode="numeric"
                  value={draft.minimumParticipationCount}
                  onChange={(e) => setDraft((c) => ({
                    ...c,
                    minimumParticipationCount: e.target.value,
                  }))}
                />
              </label>
            </div>
            <div className="journey-decision__checks">
              <label>
                <input
                  type="checkbox"
                  checked={draft.allowAbstain}
                  onChange={(e) => setDraft((c) => ({ ...c, allowAbstain: e.target.checked }))}
                />
                允許棄權
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={draft.allowVoteChange}
                  onChange={(e) => setDraft((c) => ({ ...c, allowVoteChange: e.target.checked }))}
                />
                開放投票後可改票
              </label>
            </div>

            <fieldset className="journey-decision__options">
              <legend>選項（至少 2 個）</legend>
              {draft.options.map((row, index) => (
                <div className="journey-decision__option-row" key={`opt-${index}`}>
                  <label>
                    選項 {index + 1}
                    <input
                      value={row.label}
                      maxLength={100}
                      onChange={(e) => updateOption(index, { label: e.target.value })}
                      required
                    />
                  </label>
                  <label>
                    說明（選填）
                    <input
                      value={row.description}
                      maxLength={500}
                      onChange={(e) => updateOption(index, { description: e.target.value })}
                    />
                  </label>
                  <div className="journey-decision__option-actions">
                    <button
                      type="button"
                      disabled={draft.options.length <= 2 || busy}
                      onClick={() => removeOption(index)}
                    >
                      移除
                    </button>
                  </div>
                </div>
              ))}
              <button type="button" disabled={busy} onClick={addOption}>新增選項</button>
            </fieldset>

            {formError ? <p className="journey-decision__error" role="alert">{formError}</p> : null}

            <div className="journey-decision__actions">
              <button type="submit" disabled={busy}>
                {busy ? DECISIONS_PRODUCT_CHROME.creating : DECISIONS_PRODUCT_CHROME.createDraft}
              </button>
              <Link to={`/journeys/${journey.journeyId}/decisions`}>{WORKSPACE_PRODUCT_CHROME.cancel}</Link>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}
